import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Card,
  CardDescription,
  CardTitle,
  ErrorState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  Tooltip,
  formatNumber,
  formatPercent,
} from '@mgdlead/ui';
import { ArrowLeft } from 'lucide-react';

import { PageHeader } from '@mgdlead/ui';
import { CampaignCapacityLine } from '../../../features/campaign/CampaignCapacityLine';
import { CampaignTotals, MetaFreshness } from '../../../features/campaigns/CampaignPerformance';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import { useCampaignDetail, type CampaignDetailData } from '../../../lib/api';
import { RetencaoDaCampanha } from '../../../features/retention/RetencaoDaCampanha';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import { PeriodFilter } from '../dashboard/AnalyticsShell';
import { ChartCard, LegendItem, LineChart } from '../dashboard/charts';
import {
  SERIE,
  diaCurto,
  type Intervalo,
  type Periodo,
  type PeriodoId,
} from '../dashboard/analytics-ui';

const TRACO = '—';
const COR_CLIQUES = 'var(--color-chart-3)';

/**
 * A página de UMA campanha (pedido do dono, 06/10, com a tela de um concorrente na mão).
 *
 * De cima para baixo, a ordem da pergunta de quem paga anúncio: quanto custou (CPLs) → como
 * o dia andou (cliques, entradas e saídas no tempo) → como estão os grupos (lotação, vagas).
 */
export function CampaignDetailPage() {
  const { workspace } = useWorkspace();
  const { campaignId = '' } = useParams();
  const [preset, setPreset] = useState<PeriodoId>('HOJE');
  const [intervalo, setIntervalo] = useState<Intervalo | null>(null);
  const periodo: Periodo = intervalo ?? preset;
  const detail = useCampaignDetail(workspace.id, campaignId, periodo);
  useDocumentTitle(detail.data ? `${detail.data.name} · Campanhas` : 'Campanha');

  return (
    <>
      <Link
        to={workspacePath(workspace.slug, 'campanhas')}
        className="mb-2 inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:min-h-0"
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        Campanhas
      </Link>
      <PageHeader
        title={detail.data?.name ?? 'Campanha'}
        description={
          detail.data
            ? `Código ${detail.data.code} · ${detail.data.totais.grupos} ${
                detail.data.totais.grupos === 1 ? 'grupo' : 'grupos'
              }`
            : undefined
        }
        action={
          <PeriodFilter
            value={preset}
            onChange={setPreset}
            intervalo={{ value: intervalo, onChange: setIntervalo }}
            timezone={workspace.timezone}
            inline
          />
        }
      />

      {/* D3: lotação agregada no topo — "312 de 2.000 vagas livres". */}
      {campaignId !== '' ? (
        <div className="mb-3">
          <CampaignCapacityLine workspaceId={workspace.id} campaignId={campaignId} />
        </div>
      ) : null}

      {detail.isError ? (
        <ErrorState
          title="Não foi possível carregar a campanha"
          description={humanizeError(detail.error)}
          onRetry={() => void detail.refetch()}
        />
      ) : detail.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <>
          <Conteudo data={detail.data} />
          {/* Âncora de "Ver retenção" das recomendações do dia (G4). */}
          <div id="retencao" className="mt-4 scroll-mt-4">
            <RetencaoDaCampanha
              data={detail.data.retencao}
              campaignId={campaignId}
              periodo={periodo}
            />
          </div>
        </>
      )}
    </>
  );
}

function Conteudo({ data }: { data: CampaignDetailData }) {
  const { workspace } = useWorkspace();
  const f = data.funil;
  const t = data.totais;
  const ficaram = f ? f.entradas - f.evasao.sairamNoMesmoDia : null;

  return (
    <div className="flex flex-col gap-4">
      {f ? (
        <div className="flex flex-col gap-2">
          <MetaFreshness />
          <CampaignTotals rows={[f]} vincularMetaHref={workspacePath(workspace.slug, 'campanhas')} />
        </div>
      ) : null}

      <Serie data={data} />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi
          rotulo="Visitas na LP"
          valor={f?.visitas === null || !f ? TRACO : formatNumber(f.visitas)}
          ajuda="Quem chegou na sua página, contado pela Meta (landing_page_view)."
        />
        <Kpi
          rotulo="Cliques no link"
          valor={formatNumber(f?.cliques ?? 0)}
          ajuda="Cliques no seu link de redirecionamento — contados por nós."
        />
        <Kpi
          rotulo="Página → clique"
          valor={f?.taxaDeClique == null ? TRACO : formatPercent(f.taxaDeClique, 1)}
          ajuda="De quem chegou na página, quantos clicaram no botão do grupo. Mede a PÁGINA."
        />
        <Kpi
          rotulo="Entraram e ficaram"
          valor={ficaram === null ? TRACO : formatNumber(ficaram)}
          ajuda="Entradas vindas de clique desta campanha que não saíram no mesmo dia."
        />
        <Kpi
          rotulo="Ficaram 24h"
          valor={
            f?.retencao24h.taxa == null
              ? (f?.retencao24h.pendentes ?? 0) > 0
                ? 'aguardando'
                : TRACO
              : `${formatPercent(f.retencao24h.taxa, 0)} · ${formatNumber(f.retencao24h.ficaram)}`
          }
          ajuda="Dos que entraram há mais de 24h, quantos continuavam no grupo 24h depois. Quem entrou há menos tempo ainda não entra na conta."
        />
        <Kpi
          rotulo="Entradas nos grupos"
          valor={formatNumber(t.entradasNosGrupos)}
          ajuda="TODAS as entradas nos grupos desta campanha, com ou sem clique casado (inclui orgânico)."
        />
        <Kpi
          rotulo="Saídas"
          valor={formatNumber(t.saidasNosGrupos)}
          ajuda="Quem saiu por conta própria dos grupos desta campanha no período."
        />
        <Kpi
          rotulo="Evasão (entrou e saiu)"
          valor={f?.evasao.taxa == null ? TRACO : formatPercent(f.evasao.taxa, 1)}
          ajuda="Dos que entraram por esta campanha, quantos saíram antes de o dia virar."
        />
        <Kpi
          rotulo="Crescimento"
          valor={`${t.entradasNosGrupos - t.saidasNosGrupos >= 0 ? '+' : ''}${formatNumber(
            t.entradasNosGrupos - t.saidasNosGrupos,
          )}`}
          ajuda="Entradas menos saídas nos grupos desta campanha, no período."
        />
        <Kpi
          rotulo="Participantes"
          valor={t.participantes === null ? TRACO : formatNumber(t.participantes)}
          ajuda="Soma da contagem do WhatsApp dos grupos desta campanha, agora."
        />
        <Kpi
          rotulo="Grupos"
          valor={formatNumber(t.grupos)}
          ajuda="Grupos ligados a esta campanha."
        />
        <Kpi
          rotulo="Vagas disponíveis"
          valor={t.vagas === null ? TRACO : formatNumber(t.vagas)}
          ajuda="Quanto cabe até o teto de segurança de cada grupo, somado."
        />
        <Kpi
          rotulo="Lotação"
          valor={t.lotacao === null ? TRACO : formatPercent(t.lotacao, 1)}
          ajuda="Participantes ÷ soma dos tetos de segurança dos grupos."
        />
      </div>

      <Grupos data={data} />
    </div>
  );
}

function Kpi({ rotulo, valor, ajuda }: { rotulo: string; valor: string; ajuda: string }) {
  return (
    <Card padded={false} className="p-3 md:p-4">
      <Tooltip content={ajuda}>
        <span tabIndex={0} className="cursor-help text-xs text-muted-foreground">
          {rotulo}
        </span>
      </Tooltip>
      <span className="nums mt-1 block text-xl font-semibold text-foreground">{valor}</span>
    </Card>
  );
}

function Serie({ data }: { data: CampaignDetailData }) {
  const porHora = data.granularidade === 'HORA';
  const labels = data.serie.map((p) => (porHora ? p.bucket.slice(0, 2) + 'h' : diaCurto(p.bucket)));
  const vazio = data.serie.every((p) => p.cliques + p.entradas + p.saidas === 0);
  return (
    <ChartCard
      title={porHora ? 'Hora a hora' : 'Dia a dia'}
      description="Cliques no link, entradas e saídas nos grupos desta campanha."
      legend={
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <LegendItem color={COR_CLIQUES} label="Cliques" />
          <LegendItem color={SERIE.entradas.color} label="Entradas" />
          <LegendItem color={SERIE.saidas.color} label="Saídas" />
        </span>
      }
      empty={vazio ? 'Nenhum clique, entrada ou saída nesta campanha no período.' : undefined}
      table={
        <Table caption="Série da campanha" density="dense">
          <TableHeader>
            <TableRow>
              <TableHead>{porHora ? 'Hora' : 'Dia'}</TableHead>
              <TableHead>Cliques</TableHead>
              <TableHead>Entradas</TableHead>
              <TableHead>Saídas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.serie.map((p, i) => (
              <TableRow key={p.bucket}>
                <TableRowHeader>{labels[i]}</TableRowHeader>
                <TableCell numeric>{formatNumber(p.cliques)}</TableCell>
                <TableCell numeric>{formatNumber(p.entradas)}</TableCell>
                <TableCell numeric>{formatNumber(p.saidas)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    >
      <LineChart
        labels={labels}
        measured={data.serie.map(() => true)}
        ariaLabel="Cliques, entradas e saídas da campanha no tempo"
        height={220}
        series={[
          {
            id: 'cliques',
            label: 'Cliques',
            color: COR_CLIQUES,
            values: data.serie.map((p) => p.cliques),
          },
          {
            id: 'entradas',
            label: 'Entradas',
            color: SERIE.entradas.color,
            values: data.serie.map((p) => p.entradas),
          },
          {
            id: 'saidas',
            label: 'Saídas',
            color: SERIE.saidas.color,
            values: data.serie.map((p) => p.saidas),
          },
        ]}
      />
    </ChartCard>
  );
}

function Grupos({ data }: { data: CampaignDetailData }) {
  if (data.grupos.length === 0) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Grupos</CardTitle>
        <CardDescription>
          Nenhum grupo ligado a esta campanha. Ligue em Campanhas › Grupos.
        </CardDescription>
      </Card>
    );
  }
  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>Grupos da campanha</CardTitle>
      <CardDescription>Entradas e saídas no período; pessoas e lotação agora.</CardDescription>
      <Table caption="Grupos da campanha" density="dense" className="mt-3" zebra>
        <TableHeader>
          <TableRow>
            <TableHead>Grupo</TableHead>
            <TableHead>Pessoas</TableHead>
            <TableHead>Lotação</TableHead>
            <TableHead>Entradas</TableHead>
            <TableHead>Saídas</TableHead>
            <TableHead>Saldo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.grupos.map((g) => (
            <TableRow key={g.groupId}>
              <TableRowHeader>
                <span className="block min-w-48 truncate">{g.name ?? 'Grupo sem nome'}</span>
              </TableRowHeader>
              <TableCell numeric nowrap>
                {g.participantes === null
                  ? TRACO
                  : `${formatNumber(g.participantes)} de ${formatNumber(g.teto)}`}
              </TableCell>
              <TableCell numeric>
                {g.participantes === null || g.teto <= 0
                  ? TRACO
                  : formatPercent(g.participantes / g.teto, 0)}
              </TableCell>
              <TableCell numeric>{formatNumber(g.entradas)}</TableCell>
              <TableCell numeric>{formatNumber(g.saidas)}</TableCell>
              <TableCell numeric>{formatNumber(g.entradas - g.saidas)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
